import { useState, useMemo, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { REFRESH_INTERVALS, STALE_TIMES } from '@shared/constants';
import { useToast } from '@/hooks/use-toast';
import { useWallet } from '@/contexts/wallet-context';
import { useLocation } from 'wouter';
import { apiRequest } from '@/lib/queryClient';
import { useDappSign } from '@/contexts/dapp-sign-context';
import { playSessionHeaders } from '@/lib/play-session';
import { 
  Users, Coins, Play, Bot, Globe, 
  Clock, Zap, Trophy, Star,
  UserCircle, Search, Loader2,
  LayoutGrid, List,
} from 'lucide-react';
import { CardTableIcon, AiPlayerIcon, TrophyBadgeIcon, SlotMachineIcon, SuitIcon, ShieldLockIcon, ZapBoltIcon } from '@/components/icons';
import {
  CRAFTPIX,
  tablePreviewSrc,
  tableTheme,
} from '@/lib/craftpix-poker-ui';
import {
  CraftpixStage,
  CraftpixContainer,
  CraftpixMoneyPill,
  CraftpixSpriteButton,
  CraftpixSectionTitle,
} from '@/components/craftpix-lobby-chrome';
import { LobbyWalletSidePanel } from '@/components/lobby-wallet-dash';
import { useWalletState } from '@/contexts/wallet-context';
import { BRAND } from '@shared/brand';

interface TableSeat {
  position: number;
  occupied: boolean;
  playerName?: string;
  chips?: number;
}

interface GameTable {
  id: string;
  name: string;
  type: 'cash' | 'tournament';
  format?: 'nl' | 'hu';
  stakesLabel?: string;
  noLimit?: boolean;
  stakes: {
    smallBlind: number;
    bigBlind: number;
    minBuyIn: number;
    maxBuyIn: number;
  };
  maxPlayers: number;
  currentPlayers: number;
  /** Humans only — AI practice reserves 1 seat for agent BUDB */
  humanPlayers?: number;
  aiPlayers?: number;
  seats: TableSeat[];
  status: 'waiting' | 'active' | 'full';
  description?: string;
  isAIPractice?: boolean;
  gameState?: {
    pot: number;
    round: string;
    handInProgress: boolean;
  };
}

interface TablesResponse {
  success: boolean;
  tables: GameTable[];
  totalTables: number;
  totalPlayers: number;
}

type LobbyViewMode = 'grid' | 'list';
const LOBBY_VIEW_KEY = 'budb_lobby_view';

/** Joinable seats for humans (AI practice keeps 1 seat for agent). */
function canJoinTable(table: GameTable): boolean {
  if (table.status === 'full') {
    // Re-check AI practice: full means a human is already in
    if (table.isAIPractice) {
      const humans =
        table.humanPlayers ??
        Math.max(0, table.currentPlayers - (table.aiPlayers ?? 1));
      return humans < Math.max(1, table.maxPlayers - 1);
    }
    return false;
  }
  if (table.isAIPractice) {
    const humans =
      table.humanPlayers ??
      Math.max(0, table.currentPlayers - (table.aiPlayers ?? 0));
    return humans < Math.max(1, table.maxPlayers - 1);
  }
  return table.currentPlayers < table.maxPlayers;
}

function tableMeta(table: GameTable) {
  const emptySeats = table.maxPlayers - table.currentPlayers;
  const isAI = table.isAIPractice === true;
  const isHu = table.format === 'hu' || table.maxPlayers === 2;
  const isTournament = table.type === 'tournament' && !isHu;
  const theme = tableTheme({
    isAI,
    isHu,
    isTournament,
    bigBlind: table.stakes.bigBlind,
  });
  const felt = tablePreviewSrc(theme);
  const accentColor = isAI
    ? '#c9a0ff'
    : isHu
      ? '#e8c76a'
      : isTournament || theme === 'red'
        ? '#e8a090'
        : '#9fd4b0';
  const kindLabel = isAI ? 'AI' : isHu ? 'HEADS-UP' : isTournament ? 'TOURNAMENT' : 'NL CASH';
  const kindSub = isAI
    ? 'AI Practice'
    : isHu
      ? 'Heads-Up NL'
      : isTournament
        ? 'Tournament'
        : 'No-Limit Cash';
  const joinable = canJoinTable(table);
  const statusLabel = !joinable
    ? 'Full'
    : table.status === 'waiting'
      ? isAI
        ? 'VS BUDB'
        : 'Waiting'
      : table.status === 'active'
        ? 'Live'
        : 'Open';
  const statusColor = !joinable
    ? '#ff6b6b'
    : table.status === 'active'
      ? '#5dffa0'
      : table.status === 'waiting'
        ? isAI
          ? '#c9a0ff'
          : '#ffd866'
        : '#5dffa0';
  const stakesLabel =
    table.stakesLabel || `${table.stakes.smallBlind}/${table.stakes.bigBlind}`;
  return {
    emptySeats,
    isAI,
    isHu,
    isTournament,
    theme,
    felt,
    accentColor,
    kindLabel,
    kindSub,
    statusLabel,
    statusColor,
    stakesLabel,
    joinable,
  };
}

/** PokerStars-style single-line table row */
function TableListRow({
  table,
  onJoin,
  onWatch,
}: {
  table: GameTable;
  onJoin: (table: GameTable) => void;
  onWatch: (table: GameTable) => void;
}) {
  const m = tableMeta(table);
  const canJoin = m.joinable;

  return (
    <div
      className="lobby-list-row"
      data-testid={`row-table-${table.id}`}
      onDoubleClick={() => (canJoin ? onJoin(table) : onWatch(table))}
    >
      <div className="lobby-list-row__status">
        <span
          className="lobby-list-row__dot"
          style={{ background: m.statusColor, boxShadow: `0 0 8px ${m.statusColor}` }}
        />
        <span className="lobby-list-row__status-txt">{m.statusLabel}</span>
      </div>
      <div className="lobby-list-row__name" title={table.name}>
        <span className="lobby-list-row__name-main">{table.name}</span>
        <span className="lobby-list-row__name-sub" style={{ color: m.accentColor }}>
          {m.kindSub}
        </span>
      </div>
      <div className="lobby-list-row__stakes tabular-nums">
        <span className="lobby-list-row__stakes-val">{m.stakesLabel}</span>
        <span className="lobby-list-row__muted">GBUX</span>
      </div>
      <div className="lobby-list-row__players tabular-nums">
        <span style={{ color: m.accentColor, fontWeight: 800 }}>{table.currentPlayers}</span>
        <span className="lobby-list-row__muted">/{table.maxPlayers}</span>
      </div>
      <div className="lobby-list-row__type">
        <span
          className="lobby-list-row__badge"
          style={{
            color: m.accentColor,
            borderColor: `${m.accentColor}66`,
            background: `${m.accentColor}14`,
          }}
        >
          {m.kindLabel}
        </span>
      </div>
      <div className="lobby-list-row__open tabular-nums">
        {m.emptySeats > 0 ? (
          <span style={{ color: '#9fd4b0' }}>{m.emptySeats} open</span>
        ) : (
          <span className="lobby-list-row__muted">Full</span>
        )}
      </div>
      <div className="lobby-list-row__actions">
        <button
          type="button"
          className="lobby-list-btn lobby-list-btn--join"
          disabled={!canJoin}
          onClick={() => onJoin(table)}
          data-testid={`button-join-row-${table.id}`}
        >
          Join
        </button>
        <button
          type="button"
          className="lobby-list-btn lobby-list-btn--watch"
          onClick={() => onWatch(table)}
        >
          Watch
        </button>
      </div>
    </div>
  );
}

function TableListView({
  tables,
  onJoin,
  onWatch,
}: {
  tables: GameTable[];
  onJoin: (table: GameTable) => void;
  onWatch: (table: GameTable) => void;
}) {
  if (tables.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-slate-400 text-lg">No tables found</div>
        <p className="text-slate-500 text-sm mt-2">Try adjusting your search or filters</p>
      </div>
    );
  }

  return (
    <div className="lobby-list" data-testid="lobby-table-list">
      <div className="lobby-list-head">
        <div className="lobby-list-row__status">Status</div>
        <div className="lobby-list-row__name">Table</div>
        <div className="lobby-list-row__stakes">Stakes</div>
        <div className="lobby-list-row__players">Players</div>
        <div className="lobby-list-row__type">Type</div>
        <div className="lobby-list-row__open">Seats</div>
        <div className="lobby-list-row__actions">Action</div>
      </div>
      <div className="lobby-list-body">
        {tables.map((table) => (
          <TableListRow
            key={table.id}
            table={table}
            onJoin={onJoin}
            onWatch={onWatch}
          />
        ))}
      </div>
    </div>
  );
}

function TableCard({ 
  table, 
  onJoin, 
  onWatch
}: { 
  table: GameTable; 
  onJoin: (table: GameTable) => void;
  onWatch: (table: GameTable) => void;
}) {
  const {
    emptySeats,
    isAI,
    isHu,
    isTournament,
    theme,
    felt,
    accentColor,
    kindLabel,
    kindSub,
    joinable,
    statusLabel,
    statusColor,
  } = tableMeta(table);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.22 }}
    >
      <div className="cp-table-card" data-testid={`card-table-${table.id}`}>
        <div className="cp-table-card__felt" style={{ height: 110 }}>
          <img
            src={isHu ? CRAFTPIX.generated.tableThumbHu : felt}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 40%' }}
            onError={(e) => {
              const img = e.target as HTMLImageElement;
              if (!img.src.includes('table-green')) img.src = CRAFTPIX.lobby.tableGreen;
              else img.src = CRAFTPIX.generated.tableThumbNl;
            }}
          />
          <div className="cp-table-card__felt-fade" />
          <div
            style={{
              position: 'absolute',
              top: 8,
              right: 10,
              background: 'linear-gradient(135deg, rgba(40,30,10,0.9), rgba(20,16,8,0.92))',
              border: `1px solid ${accentColor}88`,
              color: accentColor,
              fontSize: 10,
              fontWeight: 800,
              borderRadius: 6,
              padding: '3px 9px',
              letterSpacing: 1.2,
              fontFamily: 'Inter, system-ui, sans-serif',
            }}
          >
            {kindLabel}
          </div>
          <div
            style={{
              position: 'absolute',
              top: 8,
              left: 10,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: 'rgba(0,0,0,0.45)',
              borderRadius: 20,
              padding: '3px 10px',
              border: '1px solid rgba(255,255,255,0.1)',
              fontFamily: 'Inter, system-ui, sans-serif',
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: statusColor,
                boxShadow: '0 0 8px currentColor',
              }}
            />
            <span style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.8)' }}>
              {table.status === 'waiting' ? 'Waiting' : table.status === 'active' ? 'Live' : 'Full'}
            </span>
          </div>
        </div>

        <div className="cp-table-card__body">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                background: `${accentColor}18`,
                border: `1px solid ${accentColor}44`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {isAI ? (
                <AiPlayerIcon size={16} color={accentColor} />
              ) : isTournament ? (
                <TrophyBadgeIcon size={16} color={accentColor} />
              ) : (
                <CardTableIcon size={16} color={accentColor} />
              )}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: '#fff8e7', fontWeight: 800, fontSize: 15, letterSpacing: 0.2 }}>
                {table.name}
              </div>
              <div
                style={{
                  color: accentColor,
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 1.1,
                }}
              >
                {kindSub}
              </div>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              marginBottom: 12,
            }}
          >
            <div>
              <div
                style={{
                  color: '#e8c76a',
                  fontWeight: 900,
                  fontSize: 26,
                  letterSpacing: -0.5,
                  fontFamily: 'Cinzel, serif',
                }}
              >
                {table.stakes.smallBlind}/{table.stakes.bigBlind}
              </div>
              <div
                style={{
                  color: 'rgba(255,248,231,0.4)',
                  fontSize: 10,
                  textTransform: 'uppercase',
                  letterSpacing: 1.2,
                }}
              >
                GBUX Blinds
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ color: '#fff', fontWeight: 800, fontSize: 16 }}>
                <span style={{ color: accentColor }}>{table.currentPlayers}</span>
                <span style={{ color: 'rgba(255,255,255,0.28)' }}>/{table.maxPlayers}</span>
              </div>
              <div style={{ color: 'rgba(255,248,231,0.4)', fontSize: 10 }}>Players</div>
            </div>
          </div>

          <div
            style={{
              background: 'rgba(0,0,0,0.35)',
              borderRadius: 12,
              padding: '10px 8px',
              display: 'flex',
              gap: 6,
              justifyContent: 'center',
              marginBottom: 12,
              flexWrap: 'wrap',
              border: '1px solid rgba(212,175,55,0.12)',
            }}
          >
            {table.seats.slice(0, table.maxPlayers).map((seat, idx) => (
              <div
                key={idx}
                title={seat.occupied ? seat.playerName : `Seat ${seat.position} — Empty`}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 700,
                  background: seat.occupied
                    ? `linear-gradient(135deg, ${accentColor}cc, ${accentColor}66)`
                    : 'rgba(255,255,255,0.04)',
                  border: seat.occupied
                    ? `1px solid ${accentColor}`
                    : '1.5px dashed rgba(255,255,255,0.14)',
                  color: seat.occupied ? '#1a1408' : 'rgba(255,255,255,0.25)',
                }}
              >
                {seat.occupied ? '●' : seat.position}
              </div>
            ))}
          </div>

          <div
            style={{
              display: 'flex',
              gap: 6,
              marginBottom: 12,
              fontSize: 11,
              flexWrap: 'wrap',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                color: 'rgba(255,248,231,0.55)',
                background: 'rgba(255,255,255,0.04)',
                borderRadius: 6,
                padding: '4px 8px',
                border: '1px solid rgba(212,175,55,0.1)',
              }}
            >
              <Coins className="w-3 h-3" />
              {table.stakes.minBuyIn}–{table.stakes.maxBuyIn} buy-in
            </div>
            {table.gameState?.handInProgress && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  color: '#e8c76a',
                  background: 'rgba(232,199,106,0.08)',
                  borderRadius: 6,
                  padding: '4px 8px',
                  border: '1px solid rgba(232,199,106,0.2)',
                }}
              >
                <Zap className="w-3 h-3" />
                Pot {table.gameState.pot}
              </div>
            )}
            {emptySeats > 0 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  color: '#9fd4b0',
                  background: 'rgba(159,212,176,0.08)',
                  borderRadius: 6,
                  padding: '4px 8px',
                  border: '1px solid rgba(159,212,176,0.18)',
                }}
              >
                <Clock className="w-3 h-3" />
                {emptySeats} open
              </div>
            )}
          </div>

          <div className="flex items-center justify-center gap-2 flex-wrap">
            <CraftpixSpriteButton
              src={CRAFTPIX.lobby.btnPlay}
              alt="Sit"
              label={joinable ? (isAI ? 'VS BUDB' : 'Sit') : 'Full'}
              disabled={!joinable}
              onClick={() => onJoin(table)}
              data-testid={`button-join-${table.id}`}
              className="cp-sprite-btn--lg"
            />
            {(table.status === 'active' || !joinable) && (
              <CraftpixSpriteButton
                src={CRAFTPIX.lobby.btnWatch}
                alt="Watch"
                label="Watch"
                onClick={() => onWatch(table)}
                data-testid={`button-watch-${table.id}`}
              />
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function SeatSelectionDialog({
  table,
  open,
  onOpenChange,
  onSelectSeat,
  isPending
}: {
  table: GameTable | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectSeat: (seatNumber: number, buyIn: number) => void;
  isPending: boolean;
}) {
  const [selectedSeat, setSelectedSeat] = useState<number | null>(null);
  const [buyInAmount, setBuyInAmount] = useState('');

  if (!table) return null;

  const handleConfirm = () => {
    if (selectedSeat && buyInAmount) {
      onSelectSeat(selectedSeat, parseFloat(buyInAmount));
    }
  };

  const isAI = table.isAIPractice === true;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md border-amber-500/25 bg-[#0a0e18]/95 p-0 overflow-hidden text-amber-50">
        <div
          className="px-6 pt-6 pb-2"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(10,14,24,0.9), transparent), url(${CRAFTPIX.lobby.panelBar})`,
            backgroundSize: 'cover, 100% 100%',
          }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#fff8e7] font-[Cinzel,serif]">
              {isAI ? <Bot className="w-5 h-5 text-purple-300" /> : <Globe className="w-5 h-5 text-emerald-300" />}
              {table.name}
            </DialogTitle>
            <DialogDescription className="text-amber-100/50">
              Craftpix seat select · play GBUX buy-in
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="space-y-5 px-6 py-4">
          <div>
            <label className="mb-3 block text-sm font-semibold text-amber-50/90">Choose Your Seat</label>
            <div className="grid grid-cols-6 gap-2">
              {table.seats.slice(0, table.maxPlayers).map((seat) => (
                <button
                  key={seat.position}
                  disabled={seat.occupied}
                  onClick={() => setSelectedSeat(seat.position)}
                  className={`flex w-full aspect-square flex-col items-center justify-center rounded-lg text-xs font-bold transition-all ${
                    seat.occupied
                      ? 'cursor-not-allowed bg-black/40 text-amber-100/30'
                      : selectedSeat === seat.position
                        ? 'bg-gradient-to-br from-amber-600 to-amber-800 text-white ring-2 ring-amber-300'
                        : 'bg-black/35 text-amber-50 hover:bg-amber-900/40 border border-amber-500/20'
                  }`}
                  data-testid={`seat-${seat.position}`}
                >
                  {seat.occupied ? (
                    <>
                      <UserCircle className="mb-1 h-4 w-4" />
                      <span className="w-full truncate px-1 text-[10px]">{seat.playerName?.slice(0, 6)}</span>
                    </>
                  ) : (
                    <span>{seat.position}</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-amber-50/90">
              Buy-in (GBUX play wallet)
            </label>
            <Input
              type="number"
              value={buyInAmount}
              onChange={(e) => setBuyInAmount(e.target.value)}
              placeholder={`${table.stakes.minBuyIn} - ${table.stakes.maxBuyIn}`}
              min={table.stakes.minBuyIn}
              max={table.stakes.maxBuyIn}
              className="border-amber-500/25 bg-black/40 text-lg text-amber-50"
              data-testid="input-buyin-amount"
            />
            <div className="mt-2 flex gap-2">
              {[
                table.stakes.minBuyIn,
                Math.floor((table.stakes.minBuyIn + table.stakes.maxBuyIn) / 2),
                table.stakes.maxBuyIn,
              ].map((amount) => (
                <button
                  key={amount}
                  type="button"
                  onClick={() => setBuyInAmount(amount.toString())}
                  className="flex-1 rounded border border-amber-500/20 bg-black/30 px-2 py-1 text-xs text-amber-100/70 hover:bg-amber-900/30"
                >
                  {amount}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-center gap-3 pb-2">
            <Button
              onClick={() => onOpenChange(false)}
              variant="outline"
              className="flex-1 border-amber-500/25 bg-transparent text-amber-100/80"
              disabled={isPending}
            >
              Cancel
            </Button>
            <div className="flex flex-1 justify-center">
              {isPending ? (
                <Button disabled className="w-full bg-amber-800">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Joining…
                </Button>
              ) : (
                <CraftpixSpriteButton
                  src={CRAFTPIX.lobby.btnPlay}
                  alt="Take seat"
                  label="Sit"
                  className="cp-sprite-btn--lg"
                  disabled={
                    !selectedSeat ||
                    !buyInAmount ||
                    parseFloat(buyInAmount) < table.stakes.minBuyIn
                  }
                  onClick={handleConfirm}
                  data-testid="button-confirm-seat"
                />
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Lobby(): JSX.Element {
  const [activeTab, setActiveTab] = useState('all');
  const [selectedTable, setSelectedTable] = useState<GameTable | null>(null);
  const [showSeatDialog, setShowSeatDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<LobbyViewMode>(() => {
    try {
      const v = localStorage.getItem(LOBBY_VIEW_KEY);
      if (v === 'list' || v === 'grid') return v;
    } catch {
      /* ignore */
    }
    return 'grid';
  });
  const { publicKey, connected, playGbux, canSit, refreshBalances } = useWallet();
  const { wallet } = useWalletState();
  const { ensurePlaySession } = useDappSign();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    try {
      localStorage.setItem(LOBBY_VIEW_KEY, viewMode);
    } catch {
      /* ignore */
    }
  }, [viewMode]);

  /** Phantom Connect address (play ledger key) ready for table APIs */
  const playReady = canSit || connected || wallet.canSit;
  const playKey = publicKey?.toString() || wallet.publicKey || null;

  const { data: tablesData, isLoading: tablesLoading, error: tablesError } = useQuery<TablesResponse>({
    queryKey: ['/api/tables'],
    refetchInterval: REFRESH_INTERVALS.LOBBY,
    staleTime: STALE_TIMES.LOBBY,
    enabled: true, // always list tables; sit still needs wallet
  });

  const { data: userProfile } = useQuery<{ user: any }>({
    queryKey: ['/api/user/profile', playKey],
    enabled: !!playKey,
    refetchInterval: REFRESH_INTERVALS.STATS,
    staleTime: STALE_TIMES.STATS
  });

  const joinTableMutation = useMutation({
    mutationFn: async ({
      tableId,
      gbuxAmount,
      seatNumber,
    }: {
      tableId: string;
      gbuxAmount: number;
      seatNumber: number;
    }) => {
      const walletAddress = publicKey?.toString() || wallet.publicKey;
      if (!walletAddress) throw new Error('Connect Phantom first');
      const u = userProfile?.user;
      const idempotencyKey =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `join-${Date.now()}-${Math.random().toString(36).slice(2)}`;

      // One mid-app sign → 24h play session (reuse across lobby/slots/BJ)
      await ensurePlaySession();

      const headers: Record<string, string> = {};
      playSessionHeaders(walletAddress).forEach((v, k) => {
        headers[k] = v;
      });
      headers['Content-Type'] = 'application/json';
      headers['Idempotency-Key'] = idempotencyKey;

      const response = await fetch(`/api/tables/${tableId}/join`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          walletAddress,
          buyIn: gbuxAmount,
          seatNumber,
          username: u?.username || u?.appearance?.username || walletAddress.slice(0, 8),
          avatarId: u?.avatarId || u?.appearance?.avatarId,
          nameColor: u?.nameColor || u?.appearance?.nameColor,
          frameColor: u?.frameColor || u?.appearance?.frameColor,
          avatarScale: u?.avatarScale ?? u?.appearance?.avatarScale,
          proBadgeId: u?.proBadgeId || u?.appearance?.proBadgeId,
          idempotencyKey,
        }),
      });
      const data = (await response.json()) as {
        success?: boolean;
        wsToken?: string;
        error?: string;
        [k: string]: unknown;
      };
      if (!response.ok) {
        throw new Error(data.error || `Join failed (${response.status})`);
      }
      return data as { success: boolean; wsToken?: string; [k: string]: any };
    },
    onSuccess: (data, variables) => {
      toast({
        title: "Joined Table!",
        description: `Successfully joined with ${variables.gbuxAmount} GBUX`,
      });
      // Persist the WS auth token so table.tsx can authenticate the WS connection
      if (data?.wsToken) {
        sessionStorage.setItem(`ws_token_${variables.tableId}`, data.wsToken);
      }
      setShowSeatDialog(false);
      queryClient.invalidateQueries({ queryKey: ['/api/tables'] });
      queryClient.invalidateQueries({ queryKey: ['/api/user/profile'] });
      setTimeout(() => setLocation(`/table/${variables.tableId}`), 500);
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to Join",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const user = useMemo(() => userProfile?.user, [userProfile]);
  const tables = useMemo(() => tablesData?.tables || [], [tablesData]);

  const aiTables = useMemo(() => tables.filter(t => t.isAIPractice === true), [tables]);
  const liveTables = useMemo(
    () => tables.filter((t) => t.type === 'cash' && (t.format ?? 'nl') !== 'hu'),
    [tables],
  );
  const huTables = useMemo(
    () => tables.filter((t) => t.format === 'hu' || t.maxPlayers === 2),
    [tables],
  );
  const tournamentTables = useMemo(() => tables.filter(t => t.type === 'tournament'), [tables]);

  const filteredTables = useMemo(() => {
    let filtered = tables;

    if (activeTab === 'ai') {
      filtered = aiTables;
    } else if (activeTab === 'live') {
      filtered = liveTables;
    } else if (activeTab === 'heads-up' || activeTab === 'tournament') {
      // tournament tab repurposed as heads-up if no tournaments
      filtered = huTables.length ? huTables : tournamentTables;
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          (t.stakesLabel || '').toLowerCase().includes(q) ||
          `${t.stakes.smallBlind}/${t.stakes.bigBlind}`.includes(searchQuery),
      );
    }

    // PokerStars-like list: stakes ascending, then name
    if (viewMode === 'list') {
      filtered = [...filtered].sort((a, b) => {
        const bb = a.stakes.bigBlind - b.stakes.bigBlind;
        if (bb !== 0) return bb;
        return a.name.localeCompare(b.name);
      });
    }

    return filtered;
  }, [
    activeTab,
    tables,
    aiTables,
    liveTables,
    huTables,
    tournamentTables,
    searchQuery,
    viewMode,
  ]);

  const handleJoinTable = useCallback((table: GameTable) => {
    setSelectedTable(table);
    setShowSeatDialog(true);
  }, []);

  const handleWatchTable = useCallback((table: GameTable) => {
    setLocation(`/table/${table.id}?watch=true`);
  }, [setLocation]);

  const handleSelectSeat = useCallback((seatNumber: number, buyIn: number) => {
    if (!selectedTable) return;
    joinTableMutation.mutate({
      tableId: selectedTable.id,
      gbuxAmount: buyIn,
      seatNumber,
    });
  }, [selectedTable, joinTableMutation]);

  if (tablesLoading) {
    return (
      <CraftpixStage theme="green">
        <header className="cp-header-shell">
          <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px', height: 64, display: 'flex', alignItems: 'center', gap: 12 }}>
            <img src={CRAFTPIX.generated.appIcon} alt="" style={{ width: 36, height: 36, borderRadius: 9 }} />
            <div style={{ fontFamily: 'Cinzel, serif', fontWeight: 700, fontSize: 17, color: '#fff8e7' }}>{BRAND.shortName}</div>
          </div>
        </header>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px' }}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="cp-table-card"
                style={{ animation: `pulse 1.5s ${i * 0.12}s ease-in-out infinite` }}
              >
                <div className="cp-table-card__felt" style={{ background: 'rgba(255,255,255,0.06)' }} />
                <div className="cp-table-card__body">
                  <div style={{ height: 16, width: '55%', background: 'rgba(255,255,255,0.08)', borderRadius: 6, marginBottom: 10 }} />
                  <div style={{ height: 12, width: '70%', background: 'rgba(255,255,255,0.05)', borderRadius: 6 }} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <style>{`@keyframes pulse{0%,100%{opacity:1;}50%{opacity:0.45;}}`}</style>
      </CraftpixStage>
    );
  }

  if (tablesError) {
    return (
      <CraftpixStage theme="red">
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <CraftpixContainer style={{ maxWidth: 400, width: '100%', padding: '36px 32px', textAlign: 'center' }}>
            <div style={{ fontFamily: 'Cinzel, serif', fontWeight: 700, fontSize: 22, color: '#fff8e7', marginBottom: 8 }}>
              Connection Error
            </div>
            <div style={{ color: 'rgba(255,248,231,0.55)', marginBottom: 22, lineHeight: 1.55, fontFamily: 'Inter, sans-serif', fontSize: 14 }}>
              Unable to load tables. Check your connection and retry.
            </div>
            <CraftpixSpriteButton
              src={CRAFTPIX.lobby.btnPlay}
              alt="Retry"
              label="Retry"
              className="cp-sprite-btn--lg"
              onClick={() => queryClient.invalidateQueries({ queryKey: ['/api/tables'] })}
            />
          </CraftpixContainer>
        </div>
      </CraftpixStage>
    );
  }

  return (
    <CraftpixStage theme="green">
      <style>{`
        @keyframes pulseDot { 0%,100%{opacity:1;} 50%{opacity:0.4;} }
      `}</style>

      {/* Left wallet tab — frees main column for tables */}
      <LobbyWalletSidePanel
        defaultOpen={false}
        onReadySit={() => {
          void refreshBalances?.();
          document
            .getElementById('lobby-tables')
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
      />

      <header className="cp-header-shell lobby-header">
        <div className="lobby-header__inner">
          <div className="lobby-header__brand">
            <img src={CRAFTPIX.generated.appIcon} alt={BRAND.name} className="lobby-header__logo" />
            <div className="min-w-0">
              <div className="lobby-header__title" data-testid="text-logo">
                {BRAND.shortName}
              </div>
              <div className="lobby-header__sub hide-xs">
                GBUX tables · live
              </div>
            </div>
          </div>

          <div className="lobby-header__actions">
            <CraftpixMoneyPill
              amount={
                playGbux ||
                wallet.playGbux ||
                parseFloat(user?.gbuxBalance || '0') ||
                0
              }
            />
            <CraftpixSpriteButton
              src={CRAFTPIX.lobby.btnShop}
              alt="GBUX Games"
              label="Games"
              onClick={() => setLocation('/gbuxgames')}
            />
            <CraftpixSpriteButton
              src={CRAFTPIX.lobby.btnSetting}
              alt="Account"
              label={user?.username ? String(user.username).slice(0, 8) : 'Account'}
              onClick={() => setLocation('/account')}
              data-testid="button-account"
            />
          </div>
        </div>
      </header>

      <div className="lobby-tables-shell" id="lobby-tables">
        {!playReady && (
          <div className="lobby-connect-banner">
            <span>Connect a wallet to sit — tables are open for browse.</span>
          </div>
        )}

        <div className="lobby-stats-grid">
          {[
            { icon: <CardTableIcon size={20} color="#9fd4b0" />, label: 'Live', value: liveTables.length, color: '#9fd4b0' },
            { icon: <AiPlayerIcon size={20} color="#c9a0ff" />, label: 'AI', value: aiTables.length, color: '#c9a0ff' },
            { icon: <Users className="w-5 h-5" style={{ color: '#e8c76a' }} />, label: 'Online', value: tablesData?.totalPlayers || 0, color: '#e8c76a' },
            { icon: <Star className="w-5 h-5" style={{ color: '#e8c76a' }} />, label: 'Seats', value: tables.reduce((s, t) => s + (t.maxPlayers - t.currentPlayers), 0), color: '#e8c76a' },
          ].map((stat) => (
            <div key={stat.label} className="cp-stat-tile lobby-stat-tile">
              <div
                className="lobby-stat-tile__icon"
                style={{
                  background: `${stat.color}15`,
                  border: `1px solid ${stat.color}33`,
                }}
              >
                {stat.icon}
              </div>
              <div className="min-w-0">
                <div style={{ color: stat.color, fontWeight: 900, fontSize: 20, lineHeight: 1, fontFamily: 'Cinzel, serif' }}>
                  {stat.value}
                </div>
                <div style={{ color: 'rgba(255,248,231,0.4)', fontSize: 10, marginTop: 2, fontFamily: 'Inter, sans-serif' }}>
                  {stat.label}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="lobby-filters">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="lobby-filters__tabs">
            <TabsList className="cp-tabs-shell lobby-tabs-scroll">
              <TabsTrigger value="all" data-testid="tab-all">
                All ({tables.length})
              </TabsTrigger>
              <TabsTrigger value="live" data-testid="tab-live">
                <Globe className="w-3.5 h-3.5 mr-1" />
                Live
              </TabsTrigger>
              <TabsTrigger value="ai" data-testid="tab-ai">
                <Bot className="w-3.5 h-3.5 mr-1" />
                AI
              </TabsTrigger>
              <TabsTrigger value="tournament" data-testid="tab-heads-up">
                <Trophy className="w-3.5 h-3.5 mr-1" />
                HU
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="lobby-filters__end">
            <div
              className="lobby-view-toggle"
              role="group"
              aria-label="Table layout"
            >
              <button
                type="button"
                className={`lobby-view-toggle__btn ${viewMode === 'grid' ? 'is-active' : ''}`}
                onClick={() => setViewMode('grid')}
                title="Grid cards"
                data-testid="button-view-grid"
                aria-pressed={viewMode === 'grid'}
              >
                <LayoutGrid className="h-4 w-4" />
                <span className="hide-xs">Grid</span>
              </button>
              <button
                type="button"
                className={`lobby-view-toggle__btn ${viewMode === 'list' ? 'is-active' : ''}`}
                onClick={() => setViewMode('list')}
                title="List (PokerStars-style)"
                data-testid="button-view-list"
                aria-pressed={viewMode === 'list'}
              >
                <List className="h-4 w-4" />
                <span className="hide-xs">List</span>
              </button>
            </div>
            <div className="relative lobby-filters__search">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-amber-200/50" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search…"
                className="bg-black/40 border-amber-500/20 pl-9 w-full text-amber-50 placeholder:text-amber-100/30 h-10"
                data-testid="input-search"
              />
            </div>
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={`${activeTab}-${viewMode}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            {viewMode === 'list' ? (
              <TableListView
                tables={
                  activeTab === 'all'
                    ? filteredTables
                    : filteredTables
                }
                onJoin={handleJoinTable}
                onWatch={handleWatchTable}
              />
            ) : (
              <>
                {activeTab === 'all' && (
                  <>
                    {liveTables.length > 0 && (
                      <div className="mb-8">
                        <CraftpixSectionTitle accent="#9fd4b0">
                          Live Tables ·{' '}
                          {liveTables.filter((t) => t.status === 'waiting').length} waiting
                        </CraftpixSectionTitle>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {liveTables.map((table) => (
                            <TableCard
                              key={table.id}
                              table={table}
                              onJoin={handleJoinTable}
                              onWatch={handleWatchTable}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {aiTables.length > 0 && (
                      <div className="mb-8">
                        <CraftpixSectionTitle accent="#c9a0ff">
                          Practice vs AI
                        </CraftpixSectionTitle>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {aiTables.map((table) => (
                            <TableCard
                              key={table.id}
                              table={table}
                              onJoin={handleJoinTable}
                              onWatch={handleWatchTable}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {huTables.length > 0 && (
                      <div className="mb-8">
                        <CraftpixSectionTitle accent="#e8c76a">Heads-Up</CraftpixSectionTitle>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {huTables.map((table) => (
                            <TableCard
                              key={table.id}
                              table={table}
                              onJoin={handleJoinTable}
                              onWatch={handleWatchTable}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {tournamentTables.length > 0 && (
                      <div>
                        <CraftpixSectionTitle accent="#e8a090">Tournaments</CraftpixSectionTitle>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {tournamentTables.map((table) => (
                            <TableCard
                              key={table.id}
                              table={table}
                              onJoin={handleJoinTable}
                              onWatch={handleWatchTable}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {activeTab !== 'all' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredTables.length > 0 ? (
                      filteredTables.map((table) => (
                        <TableCard
                          key={table.id}
                          table={table}
                          onJoin={handleJoinTable}
                          onWatch={handleWatchTable}
                        />
                      ))
                    ) : (
                      <div className="col-span-full text-center py-12">
                        <div className="text-slate-400 text-lg">No tables found</div>
                        <p className="text-slate-500 text-sm mt-2">
                          Try adjusting your search or filters
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      <SeatSelectionDialog
        table={selectedTable}
        open={showSeatDialog}
        onOpenChange={setShowSeatDialog}
        onSelectSeat={handleSelectSeat}
        isPending={joinTableMutation.isPending}
      />
    </CraftpixStage>
  );
}
